import React from "react"

import { format } from "date-fns"
import { ChevronDown, ChevronRight, Clock, Edit, Repeat } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { TableCell, TableRow } from "@/components/ui/table"
import { useAuth } from "@/contexts/AuthContext"
import { HOUSE_OPERATOR } from "@/hooks/useAccessList"
import { formatCost } from "@/lib/routeCostCalculation"
import {
  ROUTE_KIND_PILL_CLASSES,
  getRouteKindAccentClasses,
  getRouteKindBadge,
  getRouteKindRowBgClasses,
} from "@/lib/routeKindDisplay"
import { getPlannedRouteStatusDisplay } from "@/lib/statusDisplay"
import { toneClasses } from "@/lib/tone"
import { cn } from "@/lib/utils"
import type {
  CoveredRoute,
  Location,
  PlannedRoute,
  RouteKind,
  ShiftException,
} from "@/types/collections"

import { EmptyLegsCell, LoadFactorCell, ShiftCoverCell } from "./PlanningRouteCells"
import type { NotCheckedReason } from "./usePlanningShiftCover"
import type { RouteMetrics } from "./useRouteMetrics"

interface PlanningRouteRowProps {
  route: PlannedRoute
  routeKind: RouteKind
  /** The first stand-by row of the day, which carries the divider above it. */
  startsStandbyBlock: boolean
  isExpanded: boolean
  onToggle: () => void
  onEdit: (e: React.MouseEvent) => void
  metrics: RouteMetrics
  getLocationById: (locationId: string) => Location | undefined
  loadFactorLoading: boolean
  coverLoading: boolean
  cover: ShiftException[] | undefined
  covered: CoveredRoute[] | undefined
  notCheckedReason: NotCheckedReason | null
}

/** One route on one day, as a row of the date's table. Clicking it opens its stops. */
const PlanningRouteRow: React.FC<PlanningRouteRowProps> = ({
  route,
  routeKind,
  startsStandbyBlock,
  isExpanded,
  onToggle,
  onEdit,
  metrics,
  getLocationById,
  loadFactorLoading,
  coverLoading,
  cover,
  covered,
  notCheckedReason,
}) => {
  const { can, isPartner } = useAuth()
  const firstStop = route.stops.find((s) => s.sequence === 1) || route.stops[0]
  const lastStop = route.stops[route.stops.length - 1]
  // The stop's own name stands in where the site list is not loaded -- it is the same site name,
  // carried on the stop -- so a login without that list still sees where the route ends rather
  // than its start depot twice.
  const startDepot =
    route.stops.length > 0
      ? getLocationById(firstStop.locationId)?.site_name || firstStop.name || route.depot || "-"
      : route.depot || "-"
  const endDepot =
    route.stops.length > 0
      ? getLocationById(lastStop.locationId)?.site_name || lastStop.name || route.depot || "-"
      : route.depot || "-"

  return (
    <TableRow
      className={cn(
        "hover:bg-palette-jungleGreen000 cursor-pointer",
        // Before the expanded tint, so opening a stand-by row still
        // reads as opened rather than losing its marking.
        getRouteKindRowBgClasses(routeKind),
        // On the cells, not the row, for the same reason the accent
        // moved: TableBody forces border-width:0 on the last row, and
        // the last row of the table is a stand-by one -- so a lone
        // stand-by route at the end would lose its divider too.
        startsStandbyBlock && "[&>td]:border-t-2 [&>td]:border-t-slate-400",
        isExpanded && "bg-amber-50/50",
      )}
      onClick={onToggle}
    >
      <TableCell className={cn("p-2", getRouteKindAccentClasses(routeKind))}>
        {isExpanded ? (
          <ChevronDown size={14} className="text-palette-jungleGreen400" />
        ) : (
          <ChevronRight size={14} className="text-palette-jungleGreen400" />
        )}
      </TableCell>
      <TableCell className="font-medium text-sm">
        {route.code}
        {/* That day's operator, where it is not HIVED. A partner sees only its own days. */}
        {!isPartner && route.operator && route.operator !== HOUSE_OPERATOR && (
          <div className="text-[11px] font-normal text-palette-jungleGreen500">
            {route.operator}
          </div>
        )}
      </TableCell>
      <TableCell>
        {/* Two independent facts, side by side: the plan's lifecycle
            and what kind of day it is. They never merge -- a route can
            be Draft and Stand-by at once -- so they stay two pills
            rather than one combined value. Wrapping is allowed, since
            a narrow column should stack them rather than clip one. */}
        <span className="flex flex-wrap items-center gap-1">
          <span
            className={cn(
              "px-2 py-0.5 text-xs rounded border",
              toneClasses(getPlannedRouteStatusDisplay(route.status).tone).badge,
            )}
          >
            {getPlannedRouteStatusDisplay(route.status).label}
          </span>
          {getRouteKindBadge(routeKind) && (
            <span className={ROUTE_KIND_PILL_CLASSES}>{getRouteKindBadge(routeKind)}</span>
          )}
        </span>
      </TableCell>
      <TableCell className="text-sm">
        <div className="flex items-center gap-1">
          <Clock size={12} className="text-palette-jungleGreen400" />
          {format(route.plannedStartTime, "HH:mm")} -{" "}
          {format(route.plannedEndTime, "HH:mm")}
        </div>
      </TableCell>
      <TableCell className="text-sm text-palette-jungleGreen600">{startDepot}</TableCell>
      <TableCell className="text-sm text-palette-jungleGreen600">{endDepot}</TableCell>
      <TableCell className="text-sm text-center">
        <Badge variant="outline" className="bg-palette-jungleGreen000">
          {route.totalStops}
        </Badge>
      </TableCell>
      <TableCell className="text-sm text-right">{metrics.volume.toLocaleString()}</TableCell>
      {can.costs && (
        <TableCell className="text-sm text-right">
          <LoadFactorCell loading={loadFactorLoading} capacityInfo={metrics.capacityInfo} />
        </TableCell>
      )}
      {can.shiftCover && (
        <TableCell className="text-sm">
          <ShiftCoverCell
            loading={coverLoading}
            cover={cover}
            covered={covered}
            notCheckedReason={notCheckedReason}
          />
        </TableCell>
      )}
      {can.costs && (
        <>
          <TableCell className="text-sm text-right">
            <EmptyLegsCell emptyLegs={metrics.emptyLegs} />
          </TableCell>
          <TableCell className="text-sm text-right">
            {metrics.cost ? (
              <span className="font-medium text-blue-600">
                {formatCost(metrics.cost.total_cost)}
              </span>
            ) : (
              <span className="text-palette-jungleGreen600">
                {formatCost(route.estimatedCost || 0)}
              </span>
            )}
          </TableCell>
        </>
      )}
      <TableCell className="text-center">
        {route.isRecurring ? (
          <Repeat size={14} className="mx-auto text-purple-500" />
        ) : (
          <span className="text-palette-jungleGreen300">—</span>
        )}
      </TableCell>
      {can.editRoutes && (
        <TableCell className="text-center">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
            onClick={onEdit}
          >
            <Edit size={12} className="mr-1" />
            Edit
          </Button>
        </TableCell>
      )}
    </TableRow>
  )
}

export default PlanningRouteRow
